// ConnectorPill — the AND/OR toggle pill that appears between sibling
// nodes in an expression tree. Clicking it flips the operator on the
// surrounding group.
//
// ARIA: rendered as a single `<button role="switch">` with `aria-checked`
// reflecting the OR state (so screen readers announce "switch, AND" /
// "switch, OR"). The visual still shows both labels for sighted users.
//
// PR 2b-7: pills carry a depth-based active color so users can visually
// trace nesting at a glance.
//   depth 0  — top-level (root group)        → #000000 (black)
//   depth 1  — first nested group            → #2a7869 (teal)
//   depth 2  — second nested group           → #3767ad (blue)
//   depth 3  — third nested group            → #f14720 (orange)
//   depth 4+ — deeper than that              → #000000 (black, fallback)

import { MouseEvent, KeyboardEvent } from 'react';

export type ConnectorOp = 'and' | 'or';

// Index 0 = root (black). Indices 1-3 = the colored cues for nested levels.
// Anything past index 3 falls through to black.
const PILL_DEPTH_COLORS = ['#000000', '#2a7869', '#3767ad', '#f14720'] as const;
const PILL_DEPTH_FALLBACK = '#000000';

export function colorForDepth(depth: number): string {
  if (depth < 0) return PILL_DEPTH_COLORS[0];
  return PILL_DEPTH_COLORS[depth] ?? PILL_DEPTH_FALLBACK;
}

export type ConnectorPillProps = {
  op: ConnectorOp;
  onToggle: () => void;
  /** Visual size variant. Default = 'outer' (between top-level siblings).
   *  'inner' is slightly smaller, used inside nested groups. */
  size?: 'outer' | 'inner';
  /** Layout orientation. PR 2b-5: nested groups use 'vertical' (Screen 4
   *  of mockup); top-level pills stay 'horizontal'. */
  orientation?: 'horizontal' | 'vertical';
  /** Nesting depth of the group this pill represents. Drives the active
   *  segment's background color. Default 0 (root). */
  depth?: number;
  /** When true, the pill is read-only (e.g. while saving). */
  disabled?: boolean;
  className?: string;
};

const sizeClasses = {
  outer: {
    seg: 'px-2 py-0.5 text-[9px]',
    sep: 'h-4',
    sepVertical: 'w-4',
  },
  inner: {
    seg: 'px-1.5 py-0.5 text-[8px]',
    sep: 'h-3.5',
    sepVertical: 'w-3.5',
  },
} as const;

export default function ConnectorPill({
  op,
  onToggle,
  size = 'outer',
  orientation = 'horizontal',
  depth = 0,
  disabled = false,
  className = '',
}: ConnectorPillProps) {
  const handleClick = (e: MouseEvent | KeyboardEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    onToggle();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      handleClick(e);
    }
  };

  const isAnd = op === 'and';
  const isOr = op === 'or';
  const sizing = sizeClasses[size];

  // Active segment: depth-based background (PR 2b-7). White text. Rounded
  // on the OUTER edge so the active segment looks like a filled chip.
  // Background applied via inline style because Tailwind can't generate
  // arbitrary hex classes from a runtime depth value.
  const activeBase = 'text-white';
  const activeStyle = { backgroundColor: colorForDepth(depth) };
  const inactive = 'bg-transparent text-gray-400';

  if (orientation === 'vertical') {
    // Vertical capsule: AND on top, OR on bottom. Active segment uses
    // rounded-t-full / rounded-b-full so the capsule cap reads cleanly.
    return (
      <button
        type="button"
        role="switch"
        aria-checked={isOr}
        aria-label={`Connector: ${op.toUpperCase()}. Click to switch.`}
        disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={[
          'inline-flex flex-col items-center rounded-full border border-gray-300 bg-gray-50 select-none',
          disabled ? 'opacity-60 cursor-default' : 'cursor-pointer',
          className,
        ].join(' ')}
      >
        <span
          style={isAnd ? activeStyle : undefined}
          className={[
            sizing.seg,
            'font-semibold tracking-wide transition-colors',
            isAnd ? `${activeBase} rounded-t-full` : inactive,
          ].join(' ')}
        >
          AND
        </span>
        <span aria-hidden="true" className={`h-px ${sizing.sepVertical} bg-gray-300`} />
        <span
          style={isOr ? activeStyle : undefined}
          className={[
            sizing.seg,
            'font-semibold tracking-wide transition-colors',
            isOr ? `${activeBase} rounded-b-full` : inactive,
          ].join(' ')}
        >
          OR
        </span>
      </button>
    );
  }

  // Horizontal (default).
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isOr}
      aria-label={`Connector: ${op.toUpperCase()}. Click to switch.`}
      data-tour="connector-pill"
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={[
        'inline-flex items-center rounded-full border border-gray-300 bg-gray-50 select-none',
        disabled ? 'opacity-60 cursor-default' : 'cursor-pointer',
        className,
      ].join(' ')}
    >
      <span
        style={isAnd ? activeStyle : undefined}
        className={[
          sizing.seg,
          'font-semibold tracking-wide transition-colors',
          isAnd ? `${activeBase} rounded-l-full` : inactive,
        ].join(' ')}
      >
        AND
      </span>
      <span aria-hidden="true" className={`w-px ${sizing.sep} bg-gray-300`} />
      <span
        style={isOr ? activeStyle : undefined}
        className={[
          sizing.seg,
          'font-semibold tracking-wide transition-colors',
          isOr ? `${activeBase} rounded-r-full` : inactive,
        ].join(' ')}
      >
        OR
      </span>
    </button>
  );
}
